import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { properties } from '@/utils/properties';
import { Router } from '@angular/router';
import { Util } from '@/utils/util';
import { Colaborador } from '@/store/catalogos/colaborador';
import { MatDialog } from '@angular/material/dialog';
import { InactividadDialogComponent } from '@/shared/dialogs/inactividad-dialog/inactividad-dialog.component';
import { InactivityService } from '@services/InactivityService ';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  /* ────────────────────────────  Config  ──────────────────────────── */
  private readonly baseUrl = properties.URL_LOGIN + 'auth';
  private tokenKey = 'authToken';
  private refreshTokenKey = 'refreshToken';

  /* ────────────────────────────  CTOR  ────────────────────────────── */
  constructor(
    private http: HttpClient,
    private router: Router,
    private util: Util,
    private dialog: MatDialog,
    private inactivityService: InactivityService
  ) { }

  /* ────────────────────────────  API  ─────────────────────────────── */
  login(credentials: { username: string; password: string }) {
    return this.http.post(`${this.baseUrl}/login`, credentials,
      { observe: 'response' });
  }
  validate2FA(u: string, c: number) { return this.http.post(`${this.baseUrl}/2fa/validate`, { username: u, codigo: c }); }
  activate2FA(u: string, c: number) { return this.http.post(`${this.baseUrl}/2fa/activate`, { username: u, codigo: c }); }

  refreshToken(): Observable<any> {
    const token = this.getRefreshToken();
    if (!token) return throwError(() => new Error('Refresh token no disponible'));
    return this.http.post(`${this.baseUrl}/refresh-token`, { refreshToken: token })
      .pipe(catchError((e: HttpErrorResponse) => {
        console.error('❌ Error en refresh token', e);
        return throwError(() => new Error('No se pudo renovar la sesión'));
      }));
  }

  /* ───────────────────  Helpers de almacenamiento ─────────────────── */
  setToken(t: string) { sessionStorage.setItem(this.tokenKey, t); }
  getToken(): string | null { return sessionStorage.getItem(this.tokenKey); }
  getAuthToken(): string | null { return this.getToken(); }

  setRefreshToken(t: string) { sessionStorage.setItem(this.refreshTokenKey, t); }
  getRefreshToken(): string | null { return sessionStorage.getItem(this.refreshTokenKey); }

  /* ─────────────────────  Estado de autenticación ─────────────────── */
  isAuthenticated(): boolean {
    const tok = this.getAuthToken();
    return !!tok && !this.isTokenExpired(tok);
  }

  isTokenExpired(token: string): boolean {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp < (Date.now() / 1000);
    } catch (e) {
      console.error('❌ Token mal formado', e);
      this.logout();
      return true;
    }
  }

  /* ───────────────────────────  Logout  ───────────────────────────── */
  logout(): void {
    clearTimeout(this.refreshTimeout);
   // this.removeInactivityListeners();
this.inactivityService.stopMonitoring();
    sessionStorage.clear();
    localStorage.removeItem('menu');

    this.router.navigate(['/login'], { replaceUrl: true })
      .then(() => setTimeout(() => location.reload(), 50));
  }

  /** Llama al endpoint /logout para revocar el refresh-token */
  logoutRemoto(): void {
    const rt = this.getRefreshToken();
    if (!rt) { this.logout(); return; }

    this.http.post(`${this.baseUrl}/logout`, { refreshToken: rt })
      .subscribe({ next: () => this.logout(), error: () => this.logout() });
  }

  /* ─────────────────────────  Inicio sesión  ──────────────────────── */
  iniciarSesion(res: any): void {
    if (!res?.token || !res?.refreshToken) {
      console.error('⚠️ Respuesta de inicio de sesión incompleta', res);
      return;
    }

    console.log("menu");

    console.log(res.menu);

    this.setToken(res.token);
    this.setRefreshToken(res.refreshToken);
    this.setMenu(res.menu);
    this.setColaborador(res.colaborador);
    this.setLogo(res.logo);
    this.setPhoto(res.photo);

    this.scheduleTokenRefresh(res.token);
  //this.initInactivityMonitor();
 // this.inactivityService.startMonitoring();
  }

  isLoggedIn(): boolean {
  const token = sessionStorage.getItem('authToken');
  return !!token;
}

  /* ─────────────────────────────  Menú  ───────────────────────────── */
  setMenu(menu: any) { localStorage.setItem('menu', JSON.stringify(menu)); }
  getMenu(): any[] { const m = localStorage.getItem('menu'); return m ? JSON.parse(m) : []; }
  getMenuAdapted(): any[] { const m = localStorage.getItem('menu'); return m ? this.adaptMenu(JSON.parse(m)) : []; }

  private adaptMenu(items: any[]): any[] {
    return items.map(i => ({
      name: i.nombre, iconClasses: i.icon, path: i.path ? [i.path] : null,
      children: i.children ? this.adaptMenu(i.children) : []
    }));
  }

  /* ────────────────  Colaborador / logo / foto  ───────────────────── */
  setColaborador(c: Colaborador) { sessionStorage.setItem('colaborador', JSON.stringify(c)); }
  getSesion(): Colaborador | null {
    const raw = sessionStorage.getItem('colaborador');
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  }
  setLogo(l: string) { sessionStorage.setItem('logo', JSON.stringify(l)); }
  getLogo(): string { const r = sessionStorage.getItem('logo'); return r ? JSON.parse(r) : null; }
  setPhoto(p: string) { sessionStorage.setItem('photo', JSON.stringify(p)); }
  getPhoto(): string { const r = sessionStorage.getItem('photo'); return r ? JSON.parse(r) : null; }

  /* ───────────────────────  Auto-refresh token  ───────────────────── */
  private refreshTimeout: any;

  private scheduleTokenRefresh(token: string): void {
    try {
      const exp = JSON.parse(atob(token.split('.')[1])).exp * 1000;
      const delay = exp - Date.now() - 60_000; // 1 min antes
      if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
      (delay <= 0) ? this.forceRefresh()
        : this.refreshTimeout = setTimeout(() => this.forceRefresh(), delay);
    } catch (e) { console.error('Error programando refresh', e); this.logout(); }
  }

  private forceRefresh(): void {
    this.refreshToken().subscribe({
      next: r => {
        if (r?.token) {
          this.setToken(r.token);
          this.setRefreshToken(r.refreshToken);
          this.scheduleTokenRefresh(r.token);
        } else { this.logout(); }
      },
      error: () => this.logout()
    });
  }

  /* ─────────────────────  Inactividad usuario  ────────────────────── */
  // private inactivityTimeout: any;
  // private readonly INACTIVITY_LIMIT_MS = 2 * 60 * 1000; 
  // private boundResetTimer = this.resetInactivityTimer.bind(this);

  // private initInactivityMonitor(): void {
  //   console.log('✅ Monitoreo de inactividad iniciado');
  //   this.resetInactivityTimer();
  //   ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
  //     window.addEventListener(ev, this.boundResetTimer, { passive: true })
  //   );
  // }

  // private countdownInterval: any;
  // private countdownSeconds: number = 0;

  // private resetInactivityTimer(): void {
  //   clearTimeout(this.inactivityTimeout);
  //   clearInterval(this.countdownInterval); // 🔁 Reinicia cuenta regresiva

  //   const now = new Date().toLocaleTimeString();
  //   console.log(`👆 Actividad detectada: ${now}`);
  //   console.log(`🔁 Temporizador reiniciado (${this.INACTIVITY_LIMIT_MS / 1000} segundos)`);

  //   this.inactivityTimeout = setTimeout(() => {
  //     console.warn('⚠️ Usuario inactivo – mostrando advertencia');
  //     this.mostrarAdvertenciaInactividad();
  //     this.countdownSeconds = 60;
  //     this.countdownInterval = setInterval(() => {
  //       console.log(`⏳ Tiempo para cerrar sesión: ${this.countdownSeconds} segundos`);
  //       this.countdownSeconds--;

  //       if (this.countdownSeconds < 0) {
  //         clearInterval(this.countdownInterval);
  //         console.warn('⏱️ Sesión cerrada automáticamente (sin respuesta)');
  //         this.logoutRemoto();
  //       }
  //     }, 1000);
  //   }, this.INACTIVITY_LIMIT_MS);
  // }

  // private removeInactivityListeners(): void {
  //   ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
  //     window.removeEventListener(ev, this.boundResetTimer)
  //   );
  // }

  // private isModalOpen = false;
  // private mostrarAdvertenciaInactividad(): void {
  //   if (this.isModalOpen) return;
  //   this.isModalOpen = true;

  //   const ref = this.dialog.open(InactividadDialogComponent, {
  //     disableClose: true,
  //     width: '380px'
  //   });

  //   ref.afterClosed().subscribe(resp => {
  //      this.isModalOpen = false; 
  //     if (resp === true) {
  //       console.log('🔄 Usuario activo – temporizador reiniciado');
  //       this.resetInactivityTimer();
  //     }
  //   });
  // }
}
